import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import { ThemeProvider } from './theme/ThemeProvider';
import { AuthGuard } from './auth/AuthGuard';
import { AdminGuard } from './auth/AdminGuard';
import { AdminTargetGate } from './auth/AdminTargetGate';
import { SignInPage } from './auth/SignInPage';
import { AppShell } from './components/AppShell';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/ui/toast';
import { HomePage } from './pages/HomePage';
import { SettingsLayout } from './pages/settings/SettingsLayout';
import { ProviderSection } from './pages/settings/sections/ProviderSection';
import { AgentSection } from './pages/settings/sections/AgentSection';
import { LlmSection } from './pages/settings/sections/LlmSection';
import { VoiceSection } from './pages/settings/sections/VoiceSection';
import { CalendarSection } from './pages/settings/sections/CalendarSection';
import { FillerSection } from './pages/settings/sections/FillerSection';
import { ApiKeysSection } from './pages/settings/sections/ApiKeysSection';
import { TestSection } from './pages/settings/sections/TestSection';
import { TransferSection } from './pages/settings/sections/TransferSection';
import { MailTemplatesPage } from './pages/settings/mail-templates/MailTemplatesPage';
import { MailTemplateEditor } from './pages/settings/mail-templates/MailTemplateEditor';
import { EventTypesPage } from './pages/admin/EventTypesPage';
import { CallsListPage } from './pages/calls/CallsListPage';
import { CallTimelinePage } from './pages/calls/CallTimelinePage';
import { TicketSettingsPage } from './pages/settings/tickets/TicketSettingsPage';
import { AdminUsersPage } from './pages/admin/UsersPage';
import { MembershipPlansPage } from './pages/admin/MembershipPlansPage';
import { AdminUsageOverviewPage } from './pages/admin/UsageOverviewPage';
import { AdminUserUsagePage } from './pages/admin/UserUsagePage';
import { AdminCostRatesPage } from './pages/admin/CostRatesPage';
import { MyUsagePage } from './pages/MyUsagePage';
import { BillingReturnPage } from './pages/BillingReturnPage';
import { AccountPage } from './pages/AccountPage';
import { AdminNumberPoolPage } from './pages/admin/NumberPoolPage';
import { AdminPlatformKeysPage } from './pages/admin/PlatformKeysPage';
import {
  AdminBillingOverviewPage,
  AdminBillingDetailPage,
} from './pages/admin/BillingOversightPage';
import { AdminDevSettingsLayout } from './pages/admin/DevSettingsLayout';
import { PlatformRuntimePage } from './pages/admin/PlatformRuntimePage';
import { AgentPresetsPage } from './pages/admin/AgentPresetsPage';
import { OnboardingConfigPage } from './pages/admin/OnboardingConfigPage';
import { AdminInvoiceSettingsPage } from './pages/admin/InvoiceSettingsPage';
import { AdminInvoicesListPage } from './pages/admin/InvoicesListPage';
import { AdminMailBroadcastPage } from './pages/admin/MailBroadcastPage';
import { AdminReconciliationInboxPage } from './pages/admin/ReconciliationInboxPage';
import { AdminUserDetailPage } from './pages/admin/UserDetailPage';
import { useUser } from './auth/AuthProvider';
import { CalendarPage } from './pages/CalendarPage';
import { TicketsPage } from './pages/tickets/TicketsPage';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
          <ToastProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/signin" element={<SignInPage />} />
                <Route
                  path="/"
                  element={
                    <AuthGuard>
                      <AppShell />
                    </AuthGuard>
                  }
                >
                  <Route index element={<HomePage />} />
                  <Route path="calendar" element={<CalendarPage />} />
                  {/* Slot view used to live at /calendar/day; it is now a
                      4th tab on the main /calendar page. Keep the URL
                      working by redirecting bookmarks. */}
                  <Route
                    path="calendar/day"
                    element={<Navigate to="/calendar" replace />}
                  />
                  <Route path="tickets" element={<TicketsPage />} />

                  {/* Self-service Verbrauch & Kosten — every signed-in user
                      sees only their OWN data here. Admins are redirected
                      to the admin overview at /admin/usage. */}
                  <Route path="usage" element={<UsageRouter />} />

                  {/* Mollie redirects here after a hosted-checkout payment. */}
                  <Route path="billing/return" element={<BillingReturnPage />} />

                  {/* Account-Seite — Passwort + Profil + zukünftige Konto-Settings. */}
                  <Route path="account" element={<AccountPage />} />

                  {/* The ONE Settings entry — flat menu, no sub-tabs.
                      Number-shop functionality lives inline under the
                      'provider' section, no separate route. */}
                  <Route path="settings" element={<SettingsLayout />}>
                    <Route index element={<Navigate to="provider" replace />} />
                    <Route path="provider" element={<ProviderSection />} />
                    <Route path="agent" element={<AgentSection />} />
                    <Route path="llm" element={<LlmSection />} />
                    <Route path="voice" element={<VoiceSection />} />
                    <Route path="calendar" element={<CalendarSection />} />
                    <Route path="filler" element={<FillerSection />} />
                    <Route path="apikeys" element={<ApiKeysSection />} />
                    <Route path="event-types" element={<EventTypesPage />} />
                    <Route path="calls" element={<CallsListPage />} />
                    <Route path="calls/:callId" element={<CallTimelinePage />} />
                    <Route path="tickets" element={<TicketSettingsPage />} />
                    <Route path="mail-templates" element={<MailTemplatesPage />} />
                    <Route path="mail-templates/:id" element={<MailTemplateEditor />} />
                    <Route path="test" element={<TestSection />} />
                    <Route path="transfer" element={<TransferSection />} />
                    {/* Old `/settings/numbers` route is kept as a redirect
                        for any browser bookmark / external link. */}
                    <Route path="numbers" element={<Navigate to="/settings/provider" replace />} />
                  </Route>

                  {/* Backwards-compatible redirects so old links keep
                      working. Each lands on the matching flat menu item. */}
                  <Route path="settings/phone" element={<Navigate to="/settings/provider" replace />} />
                  <Route path="admin/event-types" element={<Navigate to="/settings/event-types" replace />} />
                  <Route path="calls" element={<Navigate to="/settings/calls" replace />} />
                  <Route path="calls/:callId" element={<NavigateLegacyCall />} />
                  <Route path="dashboard" element={<Navigate to="/" replace />} />

                  {/* Verwaltung — Benutzer-Liste + zusammengeführte Detail-Tabs. */}
                  <Route
                    path="admin/users"
                    element={
                      <AdminGuard>
                        <AdminUsersPage />
                      </AdminGuard>
                    }
                  />
                  <Route
                    path="admin/users/:uid"
                    element={
                      <AdminGuard>
                        <AdminUserDetailPage />
                      </AdminGuard>
                    }
                  />

                  {/* Backwards-compat redirects so old links keep working. */}
                  <Route path="admin/billing" element={<Navigate to="/admin/users" replace />} />
                  <Route path="admin/billing/:uid" element={<RedirectToUserDetail />} />
                  <Route path="admin/usage" element={<Navigate to="/admin/users" replace />} />
                  <Route path="admin/usage/:uid" element={<RedirectToUserDetail />} />
                  <Route path="admin/usage/rates" element={<Navigate to="/admin/dev/platform-keys" replace />} />

                  {/* Entwicklereinstellungen — admin-only sub-tabs (Membership-Plans / Platform-Keys / Numbers / Invoices). */}
                  <Route
                    path="admin/dev"
                    element={
                      <AdminGuard>
                        <AdminDevSettingsLayout />
                      </AdminGuard>
                    }
                  >
                    <Route index element={<Navigate to="runtime" replace />} />
                    <Route path="runtime" element={<PlatformRuntimePage />} />
                    <Route path="membership-plans" element={<MembershipPlansPage />} />
                    <Route path="agent-presets" element={<AgentPresetsPage />} />
                    <Route path="onboarding" element={<OnboardingConfigPage />} />
                    <Route path="platform-keys" element={<AdminPlatformKeysPage />} />
                    <Route path="numbers" element={<AdminNumberPoolPage />} />
                    <Route path="invoice-settings" element={<AdminInvoiceSettingsPage />} />
                    <Route path="invoices" element={<AdminInvoicesListPage />} />
                    <Route path="mail-broadcast" element={<AdminMailBroadcastPage />} />
                    <Route
                      path="reconciliation"
                      element={<AdminReconciliationInboxPage />}
                    />
                  </Route>

                  {/* Old top-level admin routes redirect into the new sub-tab tree. */}
                  <Route path="admin/numbers" element={<Navigate to="/admin/dev/numbers" replace />} />
                  <Route path="admin/platform-keys" element={<Navigate to="/admin/dev/platform-keys" replace />} />
                  <Route path="admin/membership-plans" element={<Navigate to="/admin/dev/membership-plans" replace />} />

                  {/* Per-user settings tree, accessed by an admin on
                      behalf of a target user. Mirrors `/settings/*`
                      one-to-one — same components, same hooks; the
                      AdminTargetGate wraps a <TargetUidProvider> from
                      the URL :uid so all settings hooks transparently
                      switch to admin-target callables. */}
                  <Route
                    path="admin/users/:uid/settings"
                    element={
                      <AdminGuard>
                        <AdminTargetGate>
                          <SettingsLayout />
                        </AdminTargetGate>
                      </AdminGuard>
                    }
                  >
                    <Route index element={<Navigate to="provider" replace />} />
                    <Route path="provider" element={<ProviderSection />} />
                    <Route path="agent" element={<AgentSection />} />
                    <Route path="llm" element={<LlmSection />} />
                    <Route path="voice" element={<VoiceSection />} />
                    <Route path="calendar" element={<CalendarSection />} />
                    <Route path="filler" element={<FillerSection />} />
                    <Route path="apikeys" element={<ApiKeysSection />} />
                    <Route path="event-types" element={<EventTypesPage />} />
                    <Route path="calls" element={<CallsListPage />} />
                    <Route path="calls/:callId" element={<CallTimelinePage />} />
                    <Route path="tickets" element={<TicketSettingsPage />} />
                    <Route path="mail-templates" element={<MailTemplatesPage />} />
                    <Route path="mail-templates/:id" element={<MailTemplateEditor />} />
                    <Route path="test" element={<TestSection />} />
                    <Route path="transfer" element={<TransferSection />} />
                    {/* Same legacy redirect inside the admin-target tree. */}
                    <Route path="numbers" element={<Navigate to="../provider" replace />} />
                  </Route>
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </ToastProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);

/** Forwards legacy /calls/:id deep-links to the new /settings/calls/:id. */
function NavigateLegacyCall() {
  const path = window.location.pathname.replace(/^\/calls/, '/settings/calls');
  return <Navigate to={path} replace />;
}

/** Old per-user pages (/admin/billing/:uid, /admin/usage/:uid) now live as
 *  tabs inside /admin/users/:uid. We extract the uid from the URL and
 *  redirect there. */
function RedirectToUserDetail() {
  const segments = window.location.pathname.split('/').filter(Boolean);
  const uid = segments[segments.length - 1];
  return <Navigate to={`/admin/users/${uid}`} replace />;
}

/**
 * Self-vs-admin router for the "Nutzung & Kosten" sidebar entry. The user
 * asked that admins see the admin overview, regular users see their own
 * personal usage — both behind the same sidebar label. We do the redirect
 * here rather than rendering different links so deep-links to /usage stay
 * meaningful for everyone.
 */
function UsageRouter() {
  const { isAdmin, loading } = useUser();
  if (loading) return null;
  if (isAdmin) return <Navigate to="/admin/usage" replace />;
  return <MyUsagePage />;
}
